import React, { Component } from 'react'
import cx from 'classnames'
import { Subject } from 'rxjs'
import { reduce } from 'libraries/lodash'
import { saveAs } from 'file-saver'
import Dialog from '@material-ui/core/Dialog'
import Paper from '@material-ui/core/Paper'
import Fade from '@material-ui/core/Fade'
import Fab from '@material-ui/core/Fab'
import Grid from '@material-ui/core/Grid'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import SearchIcon from '@material-ui/icons/Search'
import Button from '@material-ui/core/Button'
import url from 'front-end-core/core/url'
import { MW_DEAL_DETAILS } from 'constants/controllers'
import TealToolTip from 'components/TealToolTip'
import { LayoutHistoryBack } from 'components/dealwatch/Layout'
import Icon from 'components/shared/Icon'
import PdfViewer from './PdfViewer'
import DocumentsDropdown from './DocumentsDropdown'

const style = {}

const scaleDelta = 0.1

const getNewScale = (direction, prevScale, canZoomOut) => {
  if (direction === 'in') {
    return prevScale + scaleDelta
  }
  return canZoomOut ? prevScale - scaleDelta : prevScale
}

export default class DocumentOverlay extends Component {
  state = {
    textSearch: '',
    showHeader: true,
    minScale: 0,
    currentScale: 0,
    canZoomOut: false
  }

  $search = new Subject()

  setMinScale = minScale => this.setState({ minScale })

  setCurrentScale = currentScale => this.setState({ currentScale })

  handleZoom = (direction) => () => {
    this.setState(({ currentScale: prevScale, minScale, canZoomOut }) => {
      const newScale = getNewScale(direction, prevScale, canZoomOut)
      return { currentScale: newScale, canZoomOut: newScale > minScale }
    })
  }

  searchCommand = (next) => (e) => this.$search.next(next)

  onSearchTypeChange = (e) => {
    this.setState({ textSearch: e.target.value })
  }

  onSearchKeyStroke = (e) => {
    if (e.key === 'Enter') {
      this.$search.next(true)
    }
  }

  handleDocumentSelect = (e) => {
    const doc = this.props.documents[e.target.value]
    this.props.onDocumentSelect(doc)
  }

  onDocumentDownload = () => {
    const { buffer, document: { filename } } = this.props
    saveAs(new Blob([buffer]), filename) // eslint-disable-line
  }

  toggleHeader = () => this.setState(({ showHeader }) => ({ showHeader: !showHeader }))

  renderPending = () => (
    <div className='flex w-100 h-100 flex-column justify-center'>
      <div className='tc white'>
        <Icon name='icon-clock' className='w10' />
        <div className='f2'>
          Loading document
        </div>
      </div>
    </div>
  )

  renderContent = () => {
    const {
      aol,
      buffer,
      document,
      otherDocuments,
      optionalPath,
      parameters: dealParameters,
      scrollToParam,
      deal: { dealId }
    } = this.props

    const { textSearch, showHeader, canZoomOut, currentScale } = this.state
    const parameters = reduce(
      dealParameters,
      (acc, { group }) => ({
        ...acc,
        ...group.reduce((acc, param) => ({ ...acc, [param.descriptor.name]: param }), {})
      }),
      {}
    )

    return (
      <Grid container alignContent='flex-start' className='layout-width-constraint w-100 h-100 ph9 ph6-t pb7'>
        <Grid item xs={12} className='mb6 mb4-t'>
          <LayoutHistoryBack
            cxNames='white'
            href={url(MW_DEAL_DETAILS, { aol, dealId })}
            label='Back to deal details'
          />
        </Grid>
        <Fade in={showHeader} timeout={300}>
          <div className={cx('w-100 flex flex-column-t transition-all justify-between', {
            h0: !showHeader,
            'mb5-t': showHeader
          })}
          >
            <SearchBox
              textSearch={textSearch}
              handleSearchType={this.onSearchTypeChange}
              handleSearchKeystroke={this.onSearchKeyStroke}
              searchCommand={this.searchCommand}
            />
            {otherDocuments.length > 0 && (
              <DocumentsDropdown
                {...{ dealId }}
                documents={otherDocuments}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              />
            )}
          </div>
        </Fade>
        <Grid item xs={12} className='mt5 mb6 mb4-t pv6 pv4-t bt bb b--white relative'>
          <TitleSection
            {...{ showHeader, canZoomOut }}
            title={document.documentType.displayName}
            handleZoomIn={this.handleZoom('in')}
            handleZoomOut={this.handleZoom('out')}
            handleDocumentDownload={this.onDocumentDownload}
            toggleHeader={this.toggleHeader}
          />
        </Grid>
        <Grid item xs={12} className={style.OverlayBody}>
          <PdfViewer
            currentScale={currentScale}
            setMinScale={this.setMinScale}
            setCurrentScale={this.setCurrentScale}
            pdf={buffer}
            parameters={parameters}
            scrollToParam={scrollToParam}
            optionalPath={optionalPath}
            textSearch={textSearch}
            $commands={this.$search}
          />
        </Grid>
      </Grid>
    )
  }

  render () {
    return (
      <Dialog fullScreen open classes={{ paper: 'bg-dark-cyan' }}>
        {this.props.pending ? this.renderPending() : this.renderContent()}
      </Dialog>
    )
  }
}

const TitleSection = ({
  showHeader,
  title,
  toggleHeader,
  canZoomOut,
  handleZoomIn,
  handleZoomOut,
  handleDocumentDownload
}) => (
  <>
    <span
      className={cx('dn-d flex justify-center items-center absolute left-50 top0 x--50-y--50 pointer',
        'bg-white shadow-black-1 dim-grey br-100 o-80 hover-o-100'
      )}
    >
      <Icon
        className='w8'
        name={showHeader ? 'icon-chevron-up' : 'icon-chevron-down'}
        onClick={toggleHeader}
      />
    </span>
    <div className='flex items-center justify-between'>
      <span className='white f4' data-testid='document-title'>
        {title}
      </span>
      <div className='flex'>
        <TealToolTip title='Zoom out'>
          <span>
            <Fab
              disabled={!canZoomOut}
              size='small'
              classes={{ root: 'bg-white teal ml4' }}
              onClick={handleZoomOut}
            >
              <Icon
                className='w8'
                name='icon-minus'
              />
            </Fab>
          </span>
        </TealToolTip>
        <TealToolTip title='Zoom in'>
          <Fab
            size='small'
            classes={{ root: 'bg-white teal ml4' }}
            onClick={handleZoomIn}
          >
            <Icon
              className='w8'
              name='icon-plus'
            />
          </Fab>
        </TealToolTip>
        <TealToolTip title='Download document'>
          <Fab
            size='small'
            classes={{ root: 'bg-white teal ml4' }}
            onClick={handleDocumentDownload}
          >
            <Icon
              className='w8'
              name='icon-download'
            />
          </Fab>
        </TealToolTip>
      </div>
    </div>
  </>
)

const SearchBox = ({ textSearch, handleSearchType, handleSearchKeystroke, searchCommand }) => (
  <Paper className='flex justify-between items-stretch bg-white br0 relative mr8 mr0-t mb6-t'>
    <SearchIcon className='absolute grey top-50 y--50 ml4' />
    <input
      className='pl8 b--none b--transparent min-w6'
      type='text'
      placeholder='Type to search'
      value={textSearch}
      onChange={handleSearchType}
      onKeyDown={handleSearchKeystroke}
    />
    <div className='flex'>
      <Button className='br0 ba outline-0' onClick={searchCommand(true)} disabled={!textSearch} data-testid='next-search-result'>
        <ArrowDownwardIcon />
      </Button>
      <Button className='br0 ba outline-0' onClick={searchCommand(false)} disabled={!textSearch} data-testid='prev-search-result'>
        <ArrowUpwardIcon />
      </Button>
    </div>
  </Paper>
)
